import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell from "../shell/AppShell.jsx";
import { useHierarchyTree, findSuperAgent } from "./useHierarchyTree.js";
import DetailDrawer from "../components/DetailDrawer.jsx";
import "./orgchart.css";
import "./hierarchy.css";
import "./superAgentsOverview.css";
import "./superAgentLineOrgChart.css";
import "../pages/states.css";

import PageSkeleton from "../components/Skeleton.jsx";
const CRUMB_BASE = ["Backoffice", "SuperAdmin", "Super Agents"];
const DRAWER_TITLE_ID = "loc-drawer-title";

function matchesQuery(query, ...fields) {
  if (!query) return true;
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return fields.some((field) => String(field ?? "").toLowerCase().includes(needle));
}

function plcTotals(plc) {
  const agents = plc.agents ?? [];
  return agents.reduce(
    (acc, agent) => ({
      workerCount: acc.workerCount + (agent.workerCount ?? 0),
      inviteCount: acc.inviteCount + (agent.inviteCount ?? 0),
    }),
    { workerCount: 0, inviteCount: 0 }
  );
}

/** Sums PLC/Agent/Worker/Invite counts for one Super Agent - all derived from the already-loaded tree, nothing invented. */
function superAgentTotals(superAgent) {
  const plcs = superAgent.plcs ?? [];
  return plcs.reduce(
    (acc, plc) => {
      const totals = plcTotals(plc);
      return {
        plcCount: acc.plcCount + 1,
        agentCount: acc.agentCount + (plc.agents ?? []).length,
        workerCount: acc.workerCount + totals.workerCount,
        inviteCount: acc.inviteCount + totals.inviteCount,
      };
    },
    { plcCount: 0, agentCount: 0, workerCount: 0, inviteCount: 0 }
  );
}

/**
 * /backoffice/super-agents/:superAgentId - the Worker Line Org Chart inside
 * one selected Super Agent (SuperAdmin -> Super Agent -> PLC/บมจ -> Agent).
 *
 * P086R: node detail used to sit in a permanent right-side column, which
 * compressed the org-chart canvas - the primary wide work area on this
 * page. It now opens only after clicking a node (including the root
 * Admin/SuperAdmin node), as a slide-over drawer that overlays the page
 * instead of shrinking the canvas. Node selection (which tier is expanded,
 * what the drawer shows) is page-local state, not part of the URL - only
 * :superAgentId is a route param. Search filters the PLC/Agent tiers
 * client-side against the same already-loaded tree; it never triggers a
 * new fetch.
 *
 * Contact, Safety/abnormal, e-KYC, and Worker Group have no backing API yet,
 * so every zone that would show them renders an explicit reserved state
 * instead of invented data. Invite Card stays Agent-only: this page shows a
 * read-only policy note about that rule (matching the w34 reference) but no
 * send-invite action anywhere.
 *
 * P087: the PLC drawer's inspector now includes a primary action link to
 * the real PLC/บมจ detail page at /backoffice/super-agents/:superAgentId/
 * plcs/:plcId (SuperAgentPlcDetailPage.jsx) - the next drill-down level
 * after a Super Agent line. The Super Agent and Agent drawers are
 * unchanged; there is still no Agent detail route.
 */
export default function SuperAgentLineOrgChartPage() {
  const { superAgentId } = useParams();
  const { status, data: tree, error } = useHierarchyTree("worker-intake");
  const [query, setQuery] = useState("");
  const [selectedNode, setSelectedNode] = useState(null);

  if (status === "loading") {
    return (
      <AppShell crumbs={CRUMB_BASE}>
        <PageSkeleton variant="orgChart" label="กำลังโหลดผังสายงาน..." />
      </AppShell>
    );
  }

  if (status === "error") {
    return (
      <AppShell crumbs={CRUMB_BASE}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดผังสายงานได้</h1>
          <p className="wx-state-body">
            {error?.status === 401
              ? "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่"
              : "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ"}
          </p>
        </div>
      </AppShell>
    );
  }

  const superAgent = findSuperAgent(tree, superAgentId);

  if (!superAgent) {
    return (
      <AppShell crumbs={CRUMB_BASE}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่พบ Super Agent นี้</h1>
          <p className="wx-state-body">
            Super Agent ที่คุณเปิดอาจถูกย้าย ปิดใช้งาน หรือไม่อยู่ในสิทธิ์การเข้าถึงของคุณ กรุณากลับไปเลือก Super Agent
            จากรายการ
          </p>
          <p className="wx-state-reference">Super Agent ID: {superAgentId}</p>
          <p className="wx-state-contact">
            <Link className="wx-breadcrumb-link" to="/backoffice/super-agents">
              กลับไปหน้ารายชื่อ Super Agent
            </Link>
          </p>
        </div>
      </AppShell>
    );
  }

  const crumbs = [...CRUMB_BASE, superAgent.organizationName];
  const plcs = superAgent.plcs ?? [];
  const totals = superAgentTotals(superAgent);

  const focusedPlcId =
    selectedNode?.type === "plc"
      ? selectedNode.id
      : selectedNode?.type === "agent"
      ? selectedNode.plcId
      : null;

  const focusedPlc = plcs.find((plc) => plc.organizationId === focusedPlcId) ?? null;
  const agents = focusedPlc?.agents ?? [];

  const visiblePlcs = plcs.filter(
    (plc) =>
      matchesQuery(query, plc.organizationName, plc.organizationCode) ||
      (plc.agents ?? []).some((agent) => matchesQuery(query, agent.displayName, agent.agentCode))
  );

  const visibleAgents = agents.filter(
    (agent) =>
      matchesQuery(query, agent.displayName, agent.agentCode) ||
      matchesQuery(query, focusedPlc?.organizationName, focusedPlc?.organizationCode)
  );

  function selectRoot() {
    setSelectedNode({ type: "root" });
  }
  function selectSuperAgent() {
    setSelectedNode({ type: "superAgent", id: superAgent.organizationId });
  }
  function selectPlc(plc) {
    setSelectedNode({ type: "plc", id: plc.organizationId });
  }
  function selectAgent(agent, plc) {
    setSelectedNode({ type: "agent", id: agent.agentId, plcId: plc.organizationId });
  }

  return (
    <AppShell crumbs={crumbs}>
      <div className="wx-sa-page">
        <div className="wx-sa-toolbar">
          <label className="wx-sa-search">
            <span className="wx-visually-hidden">Search line, PLC, agent</span>
            <input
              type="search"
              placeholder="Search line, PLC, agent..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <div className="wx-sa-toolbar-actions">
            <button type="button" className="wx-button wx-button--ghost" disabled title="ยังไม่เปิดใช้งาน">
              Export
            </button>
            <button
              type="button"
              className="wx-button wx-button--primary"
              disabled
              title="ยังไม่เปิดใช้งาน"
            >
              Create Change Request
            </button>
          </div>
        </div>

        <header className="wx-sa-head">
          <h1 className="wx-sa-title">ผังสายงาน Super Agent</h1>
          <p className="wx-sa-subtitle">
            เปิดมาหน้านี้ต้องเห็นทันทีว่า <strong>{superAgent.organizationName}</strong> ดูแล PLC และ Agent กลุ่มไหน
            และควรเลือกดูรายละเอียดจุดไหนก่อน
          </p>
        </header>

        <div className="wx-sa-kpis">
          <KpiCard label="PLC / บมจ" value={totals.plcCount} />
          <KpiCard label="AGENTS" value={totals.agentCount} />
          <KpiCard label="WORKERS" value={totals.workerCount} />
          <KpiCard label="INVITES" value={totals.inviteCount} />
        </div>

        <div className="wx-loc-policy-note">
          <strong>Invite card ส่งจาก Agent เท่านั้น</strong>
          <span>PLC / บมจ และ Super Agent ดูสถานะการเชิญได้ แต่ส่งแทน Agent ไม่ได้</span>
        </div>

        <section className="wx-panel wx-loc-chart-panel">
          <div className="wx-loc-panel-head">
            <h2 className="wx-panel-title">Org Chart สายงานแรงงาน</h2>
            <span className="wx-loc-panel-scope">Admin &gt; Super Agent &gt; PLC / บมจ &gt; Agent</span>
          </div>

          <div className="wx-loc-chart-wrap">
            <div className="wx-tree-canvas">
              <div className="wx-tree-tier">
                <div className="wx-tree-row">
                  <button
                    type="button"
                    className={`wx-orgchart-node wx-orgchart-node--root wx-loc-node-button${
                      selectedNode?.type === "root" ? " wx-orgchart-node--selected" : ""
                    }`}
                    onClick={selectRoot}
                  >
                    <div className="wx-orgchart-node-title">Admin</div>
                    <div className="wx-orgchart-node-code">Workmate X platform</div>
                  </button>
                </div>
              </div>

              <span className="wx-tree-connector" aria-hidden="true" />

              <div className="wx-tree-tier">
                <div className="wx-tree-tier-label">Super Agent</div>
                <div className="wx-tree-row">
                  <button
                    type="button"
                    className={`wx-orgchart-node wx-loc-node-button${
                      selectedNode?.type === "superAgent" ? " wx-orgchart-node--selected" : ""
                    }`}
                    onClick={selectSuperAgent}
                  >
                    <div className="wx-orgchart-node-title">{superAgent.organizationName}</div>
                    <div className="wx-orgchart-node-code">{superAgent.organizationCode}</div>
                    <div className="wx-node-card-metrics">
                      <div className="wx-node-card-metric">
                        <span className="wx-node-card-metric-value">{totals.workerCount}</span>
                        <span className="wx-node-card-metric-label">แรงงาน</span>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              <span className="wx-tree-connector" aria-hidden="true" />

              <div className="wx-tree-tier">
                <div className="wx-tree-tier-label">PLC / บมจ ภายใต้ {superAgent.organizationName}</div>
                {plcs.length === 0 ? (
                  <p className="wx-orgchart-placeholder">ยังไม่มี PLC / บมจ ภายใต้ Super Agent นี้</p>
                ) : visiblePlcs.length === 0 ? (
                  <p className="wx-orgchart-placeholder">ไม่พบผลลัพธ์ที่ตรงกับคำค้นหา</p>
                ) : (
                  <div className={`wx-tree-row${visiblePlcs.length > 1 ? " wx-tree-row--branching" : ""}`}>
                    {visiblePlcs.map((plc) => {
                      const total = plcTotals(plc);
                      const isFocused = focusedPlcId === plc.organizationId;
                      return (
                        <button
                          type="button"
                          key={plc.organizationId}
                          className={`wx-orgchart-node wx-loc-node-button${
                            isFocused ? " wx-orgchart-node--selected" : ""
                          }`}
                          onClick={() => selectPlc(plc)}
                        >
                          <div className="wx-orgchart-node-title">{plc.organizationName}</div>
                          <div className="wx-orgchart-node-code">{plc.organizationCode}</div>
                          <div className="wx-node-card-metrics">
                            <div className="wx-node-card-metric">
                              <span className="wx-node-card-metric-value">{(plc.agents ?? []).length}</span>
                              <span className="wx-node-card-metric-label">Agent</span>
                            </div>
                            <div className="wx-node-card-metric">
                              <span className="wx-node-card-metric-value">{total.workerCount}</span>
                              <span className="wx-node-card-metric-label">แรงงาน</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <span className="wx-tree-connector" aria-hidden="true" />

              <div className="wx-tree-tier">
                <div className="wx-tree-tier-label">
                  {focusedPlc ? `Agent ภายใต้ ${focusedPlc.organizationName}` : "Agent"}
                </div>
                {!focusedPlc ? (
                  <p className="wx-orgchart-placeholder">เลือก PLC / บมจ ด้านบนเพื่อดู Agent ที่เกี่ยวข้อง</p>
                ) : agents.length === 0 ? (
                  <p className="wx-orgchart-placeholder">ยังไม่มี Agent ภายใต้ PLC นี้</p>
                ) : visibleAgents.length === 0 ? (
                  <p className="wx-orgchart-placeholder">ไม่พบผลลัพธ์ที่ตรงกับคำค้นหา</p>
                ) : (
                  <div className={`wx-tree-row${visibleAgents.length > 1 ? " wx-tree-row--branching" : ""}`}>
                    {visibleAgents.map((agent) => {
                      const isSelected =
                        selectedNode?.type === "agent" && selectedNode.id === agent.agentId;
                      return (
                        <button
                          type="button"
                          key={agent.agentId}
                          className={`wx-orgchart-node wx-loc-node-button${
                            isSelected ? " wx-orgchart-node--selected" : ""
                          }`}
                          onClick={() => selectAgent(agent, focusedPlc)}
                        >
                          <div className="wx-orgchart-node-title">{agent.displayName}</div>
                          <div className="wx-orgchart-node-code">{agent.agentCode}</div>
                          <div className="wx-node-card-metrics">
                            <div className="wx-node-card-metric">
                              <span className="wx-node-card-metric-value">{agent.workerCount ?? 0}</span>
                              <span className="wx-node-card-metric-label">แรงงาน</span>
                            </div>
                            <div className="wx-node-card-metric">
                              <span className="wx-node-card-metric-value">{agent.inviteCount ?? 0}</span>
                              <span className="wx-node-card-metric-label">คำเชิญ</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {selectedNode?.type === "agent" && (
                <>
                  <span className="wx-tree-connector" aria-hidden="true" />
                  <div className="wx-tree-tier">
                    <div className="wx-tree-tier-label">กลุ่มแรงงาน (Worker Group)</div>
                    <p className="wx-orgchart-placeholder">ยังไม่เปิดใช้งาน</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      </div>

      <DetailDrawer
        open={selectedNode !== null}
        onClose={() => setSelectedNode(null)}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector="button.wx-orgchart-node"
      >
        {selectedNode && (
          <NodeInspector
            selection={selectedNode}
            superAgent={superAgent}
            totals={totals}
            focusedPlc={focusedPlc}
            titleId={DRAWER_TITLE_ID}
          />
        )}
      </DetailDrawer>
    </AppShell>
  );
}

function KpiCard({ label, value, reservedNote }) {
  return (
    <div className={`wx-sa-kpi${reservedNote ? " wx-sa-kpi--reserved" : ""}`}>
      <span className="wx-sa-kpi-label">{label}</span>
      {reservedNote ? (
        <span className="wx-sa-kpi-reserved">{reservedNote}</span>
      ) : (
        <strong className="wx-sa-kpi-value">{value}</strong>
      )}
    </div>
  );
}

function NodeInspector({ selection, superAgent, totals, focusedPlc, titleId }) {
  if (selection.type === "root") {
    return (
      <InspectorBody
        mark="AD"
        title="Admin"
        code="Workmate X platform"
        description="SuperAdmin เป็นบัญชีระดับสูงสุดที่เห็นทุกสายงานในระบบ หน้านี้กำลังแสดงเฉพาะสายงานของ Super Agent ที่เลือกไว้"
        rows={[{ label: "ขอบเขต", value: "SuperAdmin · All Lines" }]}
        titleId={titleId}
      />
    );
  }

  if (selection.type === "plc" && focusedPlc) {
    const total = plcTotals(focusedPlc);
    return (
      <InspectorBody
        mark="PLC"
        title={focusedPlc.organizationName}
        code={focusedPlc.organizationCode}
        description={`PLC / บมจ ภายใต้ ${superAgent.organizationName} ดูแล Agent และแรงงานในกลุ่มนี้`}
        rows={[
          { label: "Super Agent", value: superAgent.organizationName },
          { label: "Agent ในกลุ่มนี้", value: (focusedPlc.agents ?? []).length },
          { label: "แรงงานในกลุ่มนี้", value: total.workerCount },
          { label: "คำเชิญที่ส่งแล้ว", value: total.inviteCount },
        ]}
        actionLink={{
          label: "เปิดหน้า PLC / บมจ",
          to: `/backoffice/super-agents/${superAgent.organizationId}/plcs/${focusedPlc.organizationId}`,
        }}
        titleId={titleId}
      />
    );
  }

  if (selection.type === "agent" && focusedPlc) {
    const agent = (focusedPlc.agents ?? []).find((a) => a.agentId === selection.id);
    if (agent) {
      return (
        <InspectorBody
          mark="AG"
          title={agent.displayName}
          code={agent.agentCode}
          description={`Agent ภายใต้ ${focusedPlc.organizationName} · ${superAgent.organizationName}`}
          rows={[
            { label: "PLC / บมจ", value: focusedPlc.organizationName },
            { label: "Super Agent", value: superAgent.organizationName },
            { label: "แรงงานในความดูแล", value: agent.workerCount ?? 0 },
            { label: "คำเชิญที่ส่งแล้ว", value: agent.inviteCount ?? 0 },
          ]}
          extraReserved={{ label: "กลุ่มแรงงาน (Worker Group)", note: "ยังไม่เปิดใช้งาน" }}
          titleId={titleId}
        />
      );
    }
  }

  return (
    <InspectorBody
      mark="SA"
      title={superAgent.organizationName}
      code={superAgent.organizationCode}
      description="Super Agent สายงานนี้ดูแล PLC / บมจ, Agent และแรงงานทั้งหมดในสายงาน"
      rows={[
        { label: "ขอบเขต", value: "SuperAdmin · All Lines" },
        { label: "PLC / บมจ ในสายงาน", value: totals.plcCount },
        { label: "Agent ในสายงาน", value: totals.agentCount },
        { label: "แรงงานในสายงาน", value: totals.workerCount },
        { label: "คำเชิญที่ส่งแล้ว", value: totals.inviteCount },
      ]}
      titleId={titleId}
    />
  );
}

function InspectorBody({ mark, title, code, description, rows, extraReserved, actionLink, titleId }) {
  return (
    <div className="wx-sa-inspector-body">
      <div className="wx-sa-inspector-head">
        <span className="wx-badge wx-badge--info">{mark}</span>
        <div>
          <h2 id={titleId} className="wx-sa-inspector-name">
            {title}
          </h2>
          <span className="wx-sa-inspector-code">{code}</span>
        </div>
      </div>

      <p className="wx-state-body wx-loc-inspector-description">{description}</p>

      <div className="wx-kv-grid wx-sa-inspector-kv">
        {rows.map((row) => (
          <div key={row.label}>
            <div className="wx-kv-label">{row.label}</div>
            <div className="wx-kv-value">{row.value}</div>
          </div>
        ))}
      </div>

      {/* One labelled row for a not-yet-built field. Its note used to be
          "รอเชื่อมต่อ <X> API" - the endpoint's name. The row itself is worth
          keeping (it tells an operator the field is coming); the endpoint name
          is ours and now stays out of it. */}
      {extraReserved && (
        <div className="wx-sa-inspector-extra">
          <div className="wx-kv-label">{extraReserved.label}</div>
          <div className="wx-sa-reserved-inline">{extraReserved.note}</div>
        </div>
      )}

      {actionLink && (
        <Link className="wx-button wx-button--primary wx-sa-inspector-cta" to={actionLink.to}>
          {actionLink.label}
        </Link>
      )}
    </div>
  );
}
